import React, { useState, useEffect, useMemo } from 'react';
import { translate } from '../../Services/translation';
import Icon from '../Icon';
import AdditionalOrderInfo from './Checkout.AdditionalOrderInfo';
import Cart from './Checkout.Cart';
import ConfirmOrder from './Checkout.ConfirmOrder';
import DeliveryMethods from './Checkout.DeliveryMethods';
import PaymentMethods from './Checkout.PaymentMethods';
import CheckoutCustomerInfoForm from './Checkout.CustomerInfoForm';
import BusinessCustomerInfo from './Checkout.BusinessCustomerInfo';
import EmptyCart from './Checkout.EmptyCart';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import PaymentWidget from '../Payments/PaymentWidget';
import Zhipster from './Zhipster/Zhipster';
import { useDispatch } from 'react-redux';
import { load as loadCart } from '../../Actions/Cart.action';
import {
  reloadPayment,
  confirmShippingMethod,
  setCustomerDetailsConfirmed,
  setZhipsterData,
  setShippingConfirmed,
  setDelivery,
} from '../../Actions/Checkout.action';
import CheckoutStepContainer from './CheckoutStepContainer';
import CheckoutCustomerInfoLabel from './Checkout.CustomerInfoLabel';
import { triggerToast } from '../../Services/Toast.service';
import CheckoutConfirmCustomerDetailsButton from './Checkout.ConfirmCustomerDetailsButton';
import {
  CHECKOUT_DROPSHIP_COMPANY,
  CHECKOUT_DROPSHIP_PRIVATE,
} from '../../constants';
import CheckoutUpsellBlockContainer from './Checkout.UpsellBlockContainer';
import { StepListType } from '../../Reducers/Checkout.reducer';

const Checkout = () => {
  const dispatch = useDispatch();
  const { payload, errors } = useTypedSelector((state) => state.checkout);
  const cart = useTypedSelector<any>((state) => state.cart);
  const [orderRows] = useState(cart?.orderRows);
  const hasOrderRows = orderRows && Object.keys(orderRows).length > 0;
  const [stepContainerList, setStepContainerList] = useState<StepListType>(
    {}
  );
  const [isZhipsterLoading, setIsZhipsterLoading] = useState(false);
  const [showAdyenRetrySection, setShowAdyenRetrySection] =
    useState(false);
  const numProducts = hasOrderRows
    ? Object.values(cart.orderRows).length
    : 0;
  const {
    authenticated,
    message,
    isBusinessCustomer,
    paymentWidget,
    selectedDeliveryMethod,
    customerDetails,
    customerDetailsConfirmed,
    alternativeAddress,
    shippingConfirmed,
    showAlternativeAddress,
    loginUrl,
    useDropshipping,
    selectedDropshipAddressType = CHECKOUT_DROPSHIP_PRIVATE,
    zhipsterData,
    deliveryMethods,
    deliveryAddresses,
    selectedDeliveryAddressId,
    dropshipCustomerDetails,
  } = payload;

  const responseString = paymentWidget
    ? paymentWidget.responseString
    : null;
  const updateKey = paymentWidget ? paymentWidget._force_update : null;

  const onZhipsterOptionChanged = (
    dropOptionSystemId: string,
    dropPointSystemId: string,
    hasDropPoints: boolean
  ) => {
    dispatch(
      setZhipsterData(
        dropOptionSystemId,
        dropPointSystemId,
        payload.alternativeAddress?.zipCode ?? customerDetails?.zipCode,
        hasDropPoints
      )
    );
    dispatch(loadCart());
  }; 

  // Error toasts
  useEffect(() => {
    for (let key of Object.keys(errors)) {
      errors[key]?.forEach(
        (err) =>
          err != '' &&
          triggerToast({
            title: translate(`checkout.errors.${key}`),
            text: err,
            type: key == 'replacements' ? 'warning' : 'error',
            autoClose: false,
          })
      );
    }
  }, [errors]);

  useEffect(() => {
    // Toasts on adyen errors
    const urlParams = new URLSearchParams(window.location.search);
    if (
      urlParams.has('Error_name') &&
      urlParams.has('Error_paymentType')
    ) {
      triggerToast({
        title: translate('general.error'),
        text: translate('checkout.adyen.error'),
        type: 'error',
        autoClose: false,
      });
    }
  }, []);

  /**
   * Ugly workaround to detect when a "Rejected" error occurred inside the Adyen widget
   */
  useEffect(() => {
    if (showAdyenRetrySection) return;

    const observer = new MutationObserver((_) => {
      if (
        document.getElementsByClassName('adyen-checkout__status--error')
          .length > 0
      ) {
        setShowAdyenRetrySection(true);
        observer.disconnect();
      }
    });
    observer.observe(document, {
      attributes: false,
      childList: true,
      characterData: false,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [showAdyenRetrySection]);

  const shippingAddress = useMemo(() => {
    if (isBusinessCustomer && !useDropshipping) {
      return deliveryAddresses.find(
        (a) => a.systemId == selectedDeliveryAddressId
      );
    }

    return alternativeAddress && showAlternativeAddress
      ? alternativeAddress
      : customerDetails;
  }, [
    deliveryAddresses,
    selectedDeliveryAddressId,
    isBusinessCustomer,
    alternativeAddress,
    customerDetails,
    showAlternativeAddress,
    useDropshipping,
  ]);

  const customerDetailsAddressString = useMemo(() => {
    const addresses = [
      shippingAddress?.address,
      shippingAddress?.address2,
    ];
    return addresses.filter((a) => !!a).join(' ');
  }, [shippingAddress]);

  const isZhipsterSelected = useMemo(
    () => selectedDeliveryMethod?.id == 'Zhipster:CheckoutApiV2',
    [selectedDeliveryMethod?.id]
  );

  const isZhipsterDone = useMemo(
    () =>
      !!zhipsterData?.selectedDropOptionId &&
      (!zhipsterData?.hasDropPoints ||
        !!zhipsterData?.selectedDropPointId),
    [zhipsterData]
  );

  useEffect(() => {
    setStepContainerList({});
    var containers = document.querySelectorAll('.step-container');
    if (containers?.length > 0) {
      containers.forEach((container, index) => {
        if (container.id) {
          setStepContainerList((prevDict) => ({
            ...prevDict,
            [container.id]: ++index,
          }));
        }
      });
    }
  }, [deliveryMethods, shippingConfirmed, isZhipsterSelected]); 

  useEffect(() => {
    if (paymentWidget && shippingConfirmed) {
      dispatch(reloadPayment());
    }
  }, [cart.grandTotal]);

  // Empty cart render
  if (numProducts <= 0) {
    return (
      <div className="bg-gray-100 px-0 py-6 md:py-8 lg:py-10">
        <EmptyCart />
      </div>
    );
  }

  return (
    <div className="bg-gray-100 px-0 py-6 md:py-8 lg:py-10">
      <div className="lg:container px-0">
        {message && (
          <div className="mx-auto mb-6 flex flex-row items-center border border-solid border-gray-900 bg-white px-4 py-3 text-base lg:mb-6 lg:max-w-[660px] lg:rounded">
            <Icon className="mr-2 h-6 w-6" name="info" />
            <span>{message}</span>
          </div>
        )}
        <div className="flex flex-col">
          <Cart />
          {!customerDetailsConfirmed && (
              <CheckoutUpsellBlockContainer containerId="upsell1" />
          )}
          <CheckoutStepContainer
            label={
              <CheckoutCustomerInfoLabel
                stepComplete={customerDetailsConfirmed}
                showSignIn={!authenticated}
                loginUrl={loginUrl}
                isB2b={isBusinessCustomer}
              />
            }
            stepKey="customerInfo"
            showContent={true}
            stepComplete={customerDetailsConfirmed}
            stepContainerList={stepContainerList}
            onClick={() =>
              customerDetailsConfirmed &&
              dispatch(setCustomerDetailsConfirmed(false))
            }
          >
            {customerDetailsConfirmed &&
              !useDropshipping &&
              customerDetails &&
              shippingAddress && (
                <div className="flex cursor-pointer flex-col text-md leading-normal text-gray-500">
                  <div>{customerDetails.phoneNumber}</div>
                  <div>{customerDetails.email}</div>
                  <div>
                    {(!useDropshipping ||
                      selectedDropshipAddressType ==
                        CHECKOUT_DROPSHIP_PRIVATE) && (
                      <>
                        {shippingAddress.firstName}{' '}
                        {shippingAddress.lastName}
                      </>
                    )}
                    {(isBusinessCustomer ||
                      (useDropshipping &&
                        selectedDropshipAddressType ==
                          CHECKOUT_DROPSHIP_COMPANY)) && (
                      <>
                        {!!shippingAddress.companyName
                          ? shippingAddress.companyName
                          : customerDetails.companyName}
                      </>
                    )}
                  </div>
                  <div>
                    {customerDetailsAddressString},{' '}
                    {shippingAddress.zipCode} {shippingAddress.city}
                  </div>
                  <div>{shippingAddress.country}</div>
                </div>
              )}
            {customerDetailsConfirmed && useDropshipping && (
              <div className="flex cursor-pointer flex-col text-md leading-normal text-gray-500">
                <div>{dropshipCustomerDetails.phoneNumber}</div>
                <div>{dropshipCustomerDetails.email}</div>
                <div>
                  {dropshipCustomerDetails.firstName}{' '}
                  {dropshipCustomerDetails.lastName}
                </div>
                {selectedDropshipAddressType ==
                  CHECKOUT_DROPSHIP_COMPANY && (
                  <div>{dropshipCustomerDetails.companyName}</div>
                )}
                <div>
                  {`${dropshipCustomerDetails.address}${
                    dropshipCustomerDetails.address2
                      ? ' ' + dropshipCustomerDetails.address2
                      : ''
                  }`}
                  , {dropshipCustomerDetails.zipCode}{' '}
                  {dropshipCustomerDetails.city}
                </div>
                <div>{dropshipCustomerDetails.country}</div>
              </div>
            )}
            {!customerDetailsConfirmed && (
              <>
                {isBusinessCustomer ? (
                  <BusinessCustomerInfo />
                ) : (
                  <CheckoutCustomerInfoForm showSignUp={!authenticated} />
                )}
                <CheckoutConfirmCustomerDetailsButton />
              </>
            )}
          </CheckoutStepContainer>
          <CheckoutUpsellBlockContainer containerId="upsell2" />
          {(deliveryMethods.length > 1 ||
            !shippingConfirmed ||
            isZhipsterSelected) && (
            <CheckoutStepContainer
              label={translate('checkout.order.shipping')}
              stepKey="deliveryMethods"
              stepComplete={customerDetailsConfirmed && shippingConfirmed}
              showContent={customerDetailsConfirmed}
              stepContainerList={stepContainerList}
            >
              {!shippingConfirmed && <DeliveryMethods />}
              {shippingConfirmed && !isZhipsterSelected && (
                <div
                  className="cursor-pointer text-md font-light text-gray-500"
                  onClick={() => dispatch(setShippingConfirmed(false))}
                >
                  {selectedDeliveryMethod?.name}
                </div>
              )}
              <Zhipster
                onShippingOptionChanged={onZhipsterOptionChanged}
                deliveryMethodsComplete={shippingConfirmed}
                onLoadingChanged={setIsZhipsterLoading}
              />
              {!shippingConfirmed && (
                <button
                  className="btn btn--black btn--xlarge mt-4 w-full"
                  onClick={() => dispatch(confirmShippingMethod())}
                  disabled={
                    (isZhipsterSelected && !isZhipsterDone) ||
                    !selectedDeliveryMethod ||
                    isZhipsterLoading
                  }
                >
                  {translate('checkout.confirm-shipping')}
                </button>
              )}
            </CheckoutStepContainer>
          )}
          <CheckoutStepContainer
            label={
              !isBusinessCustomer
                ? translate('checkout.order.payment')
                : translate('checkout.confirm-b2b')
            }
            stepKey="paymentMethods"
            stepComplete={false}
            showContent={customerDetailsConfirmed && shippingConfirmed}
            stepContainerList={stepContainerList}
          >
            <PaymentMethods />
            <AdditionalOrderInfo showExtra={isBusinessCustomer} />
            {!paymentWidget && <ConfirmOrder />}
            {paymentWidget && (
              <>
                <PaymentWidget
                  key={updateKey}
                  responseString={responseString}
                />
                {showAdyenRetrySection && (
                  <div className="mt-3 flex gap-x-4 rounded bg-error-50 p-6 shadow">
                    <div className="flex">
                      <Icon name="info" className="h-6 w-6" />
                    </div>
                    <div>
                      <div className="mb-2 text-lg font-bold">
                        {translate('general.error')}
                      </div>
                      <div className="mb-4 text-lg font-light text-gray-600">
                        {translate('checkout.adyen.error')}
                      </div>
                      <button
                        className="font-akzidenz text-lg uppercase text-error-600"
                        onClick={() => {
                          dispatch(reloadPayment());
                          setShowAdyenRetrySection(false);
                        }}
                      >
                        {translate('general.retry')}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CheckoutStepContainer>
        </div>
      </div>
    </div>
  );
};
export default Checkout;
